import { useDroppable } from "@dnd-kit/core";
import {
  horizontalListSortingStrategy,
  SortableContext,
} from "@dnd-kit/sortable";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import CloseIcon from "@mui/icons-material/Close";
import { alpha, Box, IconButton, Stack, Typography } from "@mui/material";
import { Spacer, Toolbar } from "common";
import I18n from "i18n-js";
import Field from "./Field";
import { RowCoordinates, useFormEditor } from "./FormEditorDataProvider";
import RowMenu from "./RowMenu";
export interface RowProps {
  coordinates: RowCoordinates;
  hideRowHeader?: boolean;
  blockActions?: {
    blockIndex: number;
    handleDeleteBlock: (blockIndex: number) => void;
    handleMoveBlockByDir: (blockIndex: number, direction: any) => void;
    totalBlocks: number;
    blockName: string | null;
    isEditingBlockName: boolean;
    setIsEditingBlockName: (editing: boolean) => void;
    handleBlockChange: (changes: any, blockIndex: number) => void;
    setBlockName: (name: string | null) => void;
    handleAddRows?: (blockIndex: number) => void;
    handleAddFields?: (blockIndex: number, rowIndex: number) => void;
  };
}
export default function Row({
  coordinates,
  hideRowHeader,
  blockActions,
}: RowProps) {
  const { blockIndex, rowIndex } = coordinates;
  const { handleDeleteRow, handleMoveRowByDir, value, getRow, handleAddRows } =
    useFormEditor();
  //Sorting start
  const row = getRow(coordinates);
  const { setNodeRef } = useDroppable({
    id: String(row?.id),
    data: { coordinates },
  });
  //Sorting end

  if (!row) {
    return null;
  }

  // Smart header logic: hide row header if there's only one row in the block
  const shouldHideRowHeader = hideRowHeader;
  // Only merge actions if there's a single field AND row header is hidden
  const hasOnlyOneField = row.fields?.length === 1;
  const shouldMergeActions = shouldHideRowHeader && hasOnlyOneField;

  return (
    <SortableContext
      id={row.id}
      items={row.fields}
      strategy={horizontalListSortingStrategy}
    >
      <Box
        sx={{
          flex: 1,
          //marginBottom: 2,
          borderColor: "info.main",
          borderStyle: "dashed",
          borderWidth: shouldHideRowHeader ? 0 : 1,
          borderRadius: 1,
          padding: shouldHideRowHeader ? 0 : "3px",
          paddingTop: 0,
          minHeight: shouldHideRowHeader ? "auto" : "60px",
          backgroundColor: shouldHideRowHeader
            ? "transparent"
            : theme => alpha(theme.palette.info.main, 0.1),
        }}
        ref={setNodeRef}
      >
        {!shouldHideRowHeader && (
          <Toolbar sx={{ paddingLeft: "4px" }}>
            <Typography color="info.main" variant="caption">
              {I18n.t("FormEditor.row")}
            </Typography>
            <Spacer right />
            <IconButton
              title={I18n.t("FormEditor.moveRowDown")}
              size="small"
              onClick={() => {
                handleMoveRowByDir(blockIndex, rowIndex, "down");
              }}
              disabled={rowIndex >= (value || [])[blockIndex].rows?.length - 1}
            >
              <ArrowDownwardIcon fontSize="small" />
            </IconButton>
            <IconButton
              title={I18n.t("FormEditor.moveRowUp")}
              size="small"
              onClick={() => {
                handleMoveRowByDir(blockIndex, rowIndex, "up");
              }}
              disabled={rowIndex < 1}
            >
              <ArrowUpwardIcon fontSize="small" />
            </IconButton>
            <RowMenu rowIndex={rowIndex} blockIndex={blockIndex} row={row} />
            <IconButton
              title={I18n.t("FormEditor.removeRow")}
              size="small"
              onClick={() => handleDeleteRow({ blockIndex, rowIndex })}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Toolbar>
        )}
        <Stack direction="row" sx={{ flexWrap: "wrap" }}>
          {row.fields.map((field, fieldIndex) => {
            return (
              <Field
                key={`${rowIndex}-${fieldIndex}`}
                field={field}
                coordinates={{ ...coordinates, fieldIndex }}
                mergedActions={
                  shouldMergeActions
                    ? {
                        blockActions: {
                          ...blockActions,
                          handleAddRows, // Add the missing "Add new row" action
                        },
                      }
                    : undefined
                }
              />
            );
          })}
        </Stack>
      </Box>
    </SortableContext>
  );
}
